import { QueryStatus, useQuery } from "@tanstack/react-query";
import { useReactTable, getCoreRowModel, type ColumnDef, flexRender } from "@tanstack/react-table";
import { Star } from "@phosphor-icons/react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConsumersLove, PainPoints } from "@/components/ai-consumer-research/sub-brands/consumer-themes";
import { LockedCard } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { getScoreBarColor } from "@/lib/chart";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import type { ConsumerResearchCollectionReviewIntelligenceItem } from "@/schema/brand-os/ai-consumer-research";

const HealthBar = ({ value }: { value: number }) => (
  <div className="flex items-center gap-3">
    <Progress value={value} className="h-2 min-w-0 flex-1 bg-secondary" indicatorClassName={cn("rounded-full", getScoreBarColor(value))} />
    <span className="w-10 shrink-0 text-right text-xs text-muted-foreground">{value} %</span>
  </div>
);

const reviewColumns: ColumnDef<ConsumerResearchCollectionReviewIntelligenceItem>[] = [
  {
    accessorKey: "collection_name",
    header: "Collection",
    cell: ({ row }) => <span className="font-medium text-foreground">{row.original.collection_name}</span>,
  },
  {
    accessorKey: "average_rating",
    header: () => <span className="flex justify-center">Rating</span>,
    cell: ({ row }) => (
      <div className="flex items-center justify-center gap-1.5">
        <Star className="size-4 text-warning" weight="fill" />
        <span className="font-semibold text-foreground">{new Intl.NumberFormat("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(row.original.average_rating)}</span>
      </div>
    ),
  },
  {
    accessorKey: "total_reviews",
    header: () => <span className="flex justify-center">Reviews</span>,
    cell: ({ row }) => <div className="text-center text-muted-foreground">{new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(row.original.total_reviews)}</div>,
  },
  {
    id: "spacer",
    header: "",
    cell: () => null,
  },
  {
    accessorKey: "health",
    header: "Health",
    cell: ({ row }) => <HealthBar value={row.original.health} />,
  },
];

function ThemeGrid() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <ConsumersLove />
      <PainPoints />
    </div>
  );
}

function ProductReviewTable({ collections, description }: { collections: ConsumerResearchCollectionReviewIntelligenceItem[]; description: string }) {
  const table = useReactTable({ data: collections, columns: reviewColumns, getCoreRowModel: getCoreRowModel() });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Collection Review Intelligence</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="max-h-120 overflow-y-auto scrollbar-hidden">
        <div className="overflow-hidden rounded-lg">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className="border-none bg-muted/50 hover:bg-muted/50">
                  {hg.headers.map((header) => (
                    <TableHead key={header.id} className="h-10 text-xs font-medium text-muted-foreground">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="border-border hover:bg-muted/30">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function ProductsContent({ collections, description }: { collections: ConsumerResearchCollectionReviewIntelligenceItem[]; description: string }) {
  return (
    <div className="space-y-6">
      {collections.length ? <ProductReviewTable collections={collections} description={description} /> : <LockedCard title="Collection Review Intelligence" message={message} />}
      <ThemeGrid />
    </div>
  );
}

function ProductsComponent({ status, collections, description }: { status: QueryStatus; collections: ConsumerResearchCollectionReviewIntelligenceItem[]; description: string }) {
  switch (status) {
    case "pending":
      return (
        <div className="space-y-6">
          <ProductsSkeleton />
          <ThemeGrid />
        </div>
      );
    case "error":
      return (
        <div className="space-y-6">
          <LockedCard title="Collection Review Intelligence" message={message} />
          <ThemeGrid />
        </div>
      );
    case "success":
      return <ProductsContent collections={collections} description={description} />;
  }
}

export function Products() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.CollectionReviewIntelligence());
  const collections = data?.collections ?? [];
  const totalReviews = collections.reduce((sum, item) => sum + item.total_reviews, 0);
  const description = `Across ${collections.length} tracked collections · ${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(totalReviews)} reviews analyzed`;

  return <ProductsComponent status={status} collections={collections} description={description} />;
}

function ProductsSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48 rounded-md" />
        <Skeleton className="h-4 w-72 rounded-md" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="grid grid-cols-4 gap-4 rounded-lg border border-border/60 p-4">
              <Skeleton className="h-4 w-32 rounded-md" />
              <Skeleton className="h-4 w-12 rounded-md" />
              <Skeleton className="h-4 w-16 rounded-md" />
              <Skeleton className="h-4 w-full rounded-md" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
